import { useCallback, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Box, Typography, useTheme } from '@mui/material';
import { InfoFeedClient } from '../../helpers/client';
import { InfoFeedOverview } from 'orderme-api-integration-client';
import { apiKey, imagesUri } from '../../helpers/clientConfigs';
import { ImageLoader } from '../imgLoader/imgLoader';
import { useRouteNavigator } from '../../routes/useRouteNavigator';

export function NewsCarousel() {
  const theme = useTheme();
  const [news, setNews] = useState<InfoFeedOverview[]>([]);
  const infoFeedClient = useMemo(() => new InfoFeedClient(), []);
  const routeNavigator = useRouteNavigator();

  const fetchNews = useCallback(async () => {
    try {
      const response = await infoFeedClient.getBanner();
      setNews(response.filter((item) => !item.expired));
    } catch (error) {
      console.error('Error fetching news for carousel:', error);
    }
  }, [infoFeedClient]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleClick = (item: InfoFeedOverview) => {
    if (item.bannerLinkUrl) {
      window.open(item.bannerLinkUrl, '_blank');
    } else {
      routeNavigator.goToNewsItem(item.id, item.title);
    }
  };

  if (news.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: '20px',
        width: '100%',
        '& .swiper-button-next, & .swiper-button-prev': {
          mx: '5px',
          backgroundColor: 'rgba(255,255,255,0.7)',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          padding: '10px',
          color: 'primary.main',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        },
        '&:hover .swiper-button-next, &:hover .swiper-button-prev': {
          opacity: 1,
        },
      }}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        loop={news.length > 1}
        autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        breakpoints={{
          600: { slidesPerView: 2 },
          1000: { slidesPerView: 3 },
        }}
        style={
          {
            width: '100%',
            paddingBottom: '40px',
            '--swiper-pagination-color': theme.palette.primary.main,
          } as React.CSSProperties
        }
      >
        {news.map((item) => (
          <SwiperSlide key={item.id}>
            <Box
              onClick={() => handleClick(item)}
              sx={{
                position: 'relative',
                height: '300px',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                mx: '5px',
              }}
            >
              <ImageLoader
                src={`${imagesUri}/${item.imageId}?orderme-api-key=${apiKey}&height=500`}
                alt={item.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: '12px 16px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                }}
              >
                <Typography
                  sx={{
                    textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                    textTransform: 'uppercase',
                  }}
                >
                  {item.title}
                </Typography>
                {item.heading && (
                  <Typography
                    variant="body2"
                    sx={{
                      textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                    }}
                  >
                    {item.heading}
                  </Typography>
                )}
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}
